// Research query parser, ported from the live desk's researchQuery.ts.
// Accepts a contract, $ticker, @handle, X profile or post URL, an Axiom URL,
// or a DEX Screener pair URL (contract: Coin Checker dock input). Contract
// case is preserved here because Solana is case sensitive; EVM comparison
// belongs to identity/contract.ts.

import { isContractAddress } from './contract';

export type ResearchQuery =
  | { readonly kind: 'coin'; readonly value: string; readonly queryKind: 'contract' | 'ticker' | 'name' | 'axiom_url' | 'dexscreener_url' }
  | { readonly kind: 'x_account'; readonly value: string; readonly queryKind: 'x_handle' | 'x_profile_url' }
  | { readonly kind: 'x_post'; readonly value: string; readonly handle: string; readonly queryKind: 'x_post_url' }
  | { readonly kind: 'invalid'; readonly value: string; readonly queryKind: 'invalid'; readonly reason: string };

const MAX_LENGTH = 2048;
const MIN_NAME_LENGTH = 2;
const HANDLE = /^[A-Za-z0-9_]{1,15}$/u;
const TICKER = /^\$[A-Za-z0-9_]{1,20}$/u;
const POST_ID = /^\d{5,24}$/u;
const X_HOSTS: ReadonlySet<string> = new Set(['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com', 'mobile.twitter.com']);
const AXIOM_HOSTS: ReadonlySet<string> = new Set(['axiom.trade', 'www.axiom.trade']);
const DEXSCREENER_HOSTS: ReadonlySet<string> = new Set(['dexscreener.com', 'www.dexscreener.com']);

function invalid(value: string, reason: string): ResearchQuery {
  return { kind: 'invalid', value, queryKind: 'invalid', reason };
}

function parseUrl(input: string): URL | null {
  try {
    return new URL(input.includes('://') ? input : `https://${input}`);
  } catch {
    return null;
  }
}

function parseXUrl(url: URL, input: string): ResearchQuery {
  const segments = url.pathname.split('/').filter(Boolean);
  const handle = segments[0] ?? '';
  if (!HANDLE.test(handle)) return invalid(input, 'x_url_without_handle');
  const postId = segments[2] ?? '';
  if (segments.length === 3 && segments[1] === 'status' && POST_ID.test(postId)) {
    return { kind: 'x_post', value: postId, handle: handle.toLowerCase(), queryKind: 'x_post_url' };
  }
  return { kind: 'x_account', value: handle.toLowerCase(), queryKind: 'x_profile_url' };
}

function lastPathSegment(url: URL): string {
  return url.pathname.split('/').filter(Boolean).at(-1) ?? '';
}

// axiom.trade/<page>/<contract>, or ?address= / ?token=. The first candidate
// that is a contract shape wins.
function parseAxiomUrl(url: URL, input: string): ResearchQuery {
  const candidates = [lastPathSegment(url), url.searchParams.get('address') ?? '', url.searchParams.get('token') ?? ''];
  const address = candidates.find((candidate) => isContractAddress(candidate));
  return address !== undefined
    ? { kind: 'coin', value: address, queryKind: 'axiom_url' }
    : invalid(input, 'axiom_url_without_contract');
}

// dexscreener.com/<chain>/<pair or token address>. The last segment is an
// address on either shape; the backend resolves pair versus token.
function parseDexScreenerUrl(url: URL, input: string): ResearchQuery {
  const address = lastPathSegment(url);
  return isContractAddress(address)
    ? { kind: 'coin', value: address, queryKind: 'dexscreener_url' }
    : invalid(input, 'dexscreener_url_without_contract');
}

export function parseResearchQuery(raw: string): ResearchQuery {
  const input = raw.trim();
  if (!input) return invalid(input, 'empty');
  if (input.length > MAX_LENGTH) return invalid(input, 'too_long');

  if (input.startsWith('@')) {
    const handle = input.slice(1);
    return HANDLE.test(handle)
      ? { kind: 'x_account', value: handle.toLowerCase(), queryKind: 'x_handle' }
      : invalid(input, 'handle_invalid');
  }

  const url = parseUrl(input);
  if (url) {
    const host = url.hostname.toLowerCase();
    if (X_HOSTS.has(host)) return parseXUrl(url, input);
    if (AXIOM_HOSTS.has(host)) return parseAxiomUrl(url, input);
    if (DEXSCREENER_HOSTS.has(host)) return parseDexScreenerUrl(url, input);
  }

  if (isContractAddress(input)) return { kind: 'coin', value: input, queryKind: 'contract' };
  if (TICKER.test(input)) return { kind: 'coin', value: input.slice(1).toUpperCase(), queryKind: 'ticker' };
  return input.length >= MIN_NAME_LENGTH
    ? { kind: 'coin', value: input, queryKind: 'name' }
    : invalid(input, 'too_short');
}
